import React, { useState } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { propOr } from 'ramda';
import { injectIntl, FormattedMessage } from 'react-intl';

import AlertBox from 'components/ui/AlertBox';
import { afterSalesRule, legShape, productsShape } from 'constants/propTypes';
import classOfService from 'constants/propTypes/classOfService';
import { brandIcons } from 'constants/brandIcons';

import Information from 'assets/rebrand/icons/information.svg';

import TimetableStatus from 'components/shared/TimetableStatus';
import TimetableServiceStopsStatusSummary from 'components/shared/TimetableServiceStopsStatusSummary';

import OldJourneyAlertBox from './OldJourneyAlertBox';
import ThalysWorldBookingAlertBox from './ThalysWorldBookingAlertBox';
import FareDetailsContainer from './FareDetailsContainer';
import LoyaltyPromoTicketsAlertBox from './LoyaltyPromoTicketsAlertBox';
import ExchangedDueToAOPAlertBox from './ExchangedDueToAOPAlertBox';
import {
  AdditionalNotesIcon,
  BrandIcon,
  LegContainer,
  LegDetails,
  PassengerDetails,
  StyledAlertBox,
  StyledOriginDestination,
  StyledTooltip,
  TrainDetails,
  TrainNumber,
  TrainNumberContainer
} from './Leg.styles';
import ScheduleChangedAlert from './ScheduleChangedAlert';

import StarlightDelayAlertBox from './StarlightDelayAlertBox/StarlightDelayAlertBox';
import RecommendedStationArrivalTimeMessage from './RecommendedStationArrivalTimeMessage';
import { getOverrideMessages } from 'utils/getOverrideMessages';
import { isSNCBUnconfirmedTimetableManageBookingEnabled } from 'utils/featureFlags';
import { shouldShowSNCBUnconfirmedTimetableManageBookingBanner } from 'utils/sncbUnconfirmedTimetable';
import { brandCode } from 'constants/brandCodes';
import serviceTypeCodes from 'constants/serviceTypeCodes';

const Leg = ({
  bookingReference,
  direction,
  serviceType,
  trainNumber,
  arrivalTime,
  departureTime,
  isAFareAddOn,
  origin,
  destination,
  date,
  includeFareConditions,
  classOfService,
  afterSaleRuleOverride,
  numberOfPassengers,
  numberOfActivePassengers,
  customerTransactionRef,
  currency,
  journey,
  children,
  withExchangedProducts = [],
  status
}) => {
  const [serviceStatusMismatch, setServiceStatusMismatch] = useState(false);
  const sourceDate = moment.utc(date, moment.ISO_8601);
  const dateToDisplay = sourceDate.format('ddd Do MMMM YYYY');
  const { reasonCode } = afterSaleRuleOverride || {};
  const overrideMessage = getOverrideMessages({ reasonCode, status });
  const showSNCBUnconfirmedTimetableWarning =
    isSNCBUnconfirmedTimetableManageBookingEnabled() && shouldShowSNCBUnconfirmedTimetableManageBookingBanner(journey);
  const activePassengerCount = numberOfActivePassengers ?? numberOfPassengers;
  const passengerCountMessageId =
    numberOfPassengers > activePassengerCount ? 'journeySegment.passengerCountWithActive' : 'journeySegment.passengerCount';

  const uniqueKey = `${trainNumber}-${sourceDate.format('Y-MM-DD_HHmm')}`;
  const popupId = `timetable-details_${uniqueKey}`;

  const smallLogo = [brandCode.NS].includes(serviceType?.brandCode);

  return (
    <div data-cy={`booking-journey-${direction}`}>
      <h4>
        {dateToDisplay}
        <StyledOriginDestination>
          {` ${propOr('', 'name', origin)}`} to {propOr('', 'name', destination)} -{' '}
          <FormattedMessage id={passengerCountMessageId} values={{ numberOfPassengers, numberOfActivePassengers: activePassengerCount }} />
        </StyledOriginDestination>
      </h4>

      {overrideMessage && (
        <StyledAlertBox variant={AlertBox.variants.ERROR}>
          <FormattedMessage id={overrideMessage} />
        </StyledAlertBox>
      )}
      {serviceStatusMismatch && (
        <StyledAlertBox variant={AlertBox.variants.WARNING}>
          <FormattedMessage id={'aftersalesWarning.AOPMismatch'} />
        </StyledAlertBox>
      )}

      <OldJourneyAlertBox journey={journey} />
      <ThalysWorldBookingAlertBox journey={journey} />
      <LoyaltyPromoTicketsAlertBox journey={journey} />
      {serviceType?.code === serviceTypeCodes.SNCB && !journey?.cancelled && (
        <StyledAlertBox variant={AlertBox.variants.INFO} dataCy="sncb-flexible-travel-info">
          <FormattedMessage id="journeySegment.sncbFlexibleTravel" />
        </StyledAlertBox>
      )}
      {showSNCBUnconfirmedTimetableWarning && (
        <StyledAlertBox variant={AlertBox.variants.WARNING} dataCy="sncb-unconfirmed-timetable-warning">
          <FormattedMessage id="sncbUnconfirmedTimetable.manageBookingWarning" />
        </StyledAlertBox>
      )}
      <ExchangedDueToAOPAlertBox journey={journey} withExchangedProducts={withExchangedProducts} />

      <StarlightDelayAlertBox bookingReference={bookingReference} journey={journey} />

      <TimetableServiceStopsStatusSummary journey={journey} popupId={popupId} setServiceStatusMismatch={setServiceStatusMismatch} />

      <LegContainer>
        <LegDetails>
          <TrainDetails data-cy="train-details">
            {!isAFareAddOn ? (
              <>
                <TrainNumberContainer>
                  <TrainNumber data-for={popupId} data-tip>
                    <BrandIcon src={brandIcons[serviceType?.brandCode]} $small={smallLogo} />
                    {propOr('', 'code', serviceType)} {trainNumber}
                    <AdditionalNotesIcon src={Information} />
                    {customerTransactionRef && ` | Ref: ${customerTransactionRef}`}
                  </TrainNumber>
                  <ScheduleChangedAlert journey={journey} />
                  <RecommendedStationArrivalTimeMessage bookingReference={bookingReference} journey={journey} />
                </TrainNumberContainer>
                <p>
                  <FormattedMessage id="journeySegment.departs" />
                  &nbsp;{departureTime}&nbsp;
                  <StyledTooltip title={propOr('', 'name', origin)}>{propOr('', 'shortCode', origin)}</StyledTooltip>
                  <br />
                  <TimetableStatus journey={journey} origin />
                </p>
              </>
            ) : (
              <p>
                <FormattedMessage id="journeySegment.departs" />
                &nbsp;{departureTime}
                <br />
                <TimetableStatus journey={journey} origin />
              </p>
            )}

            {!isAFareAddOn && (
              <p>
                <FormattedMessage id="journeySegment.arrives" />
                {` ${arrivalTime} `}
                <StyledTooltip title={propOr('', 'name', destination)}>{propOr('', 'shortCode', destination)}</StyledTooltip>
                <br />
                <TimetableStatus journey={journey} destination />
              </p>
            )}
          </TrainDetails>
          <PassengerDetails>{children}</PassengerDetails>
        </LegDetails>
        <FareDetailsContainer
          classOfService={classOfService}
          includeFareConditions={includeFareConditions}
          serviceType={serviceType?.code}
          currency={currency}
          journey={journey}
          withExchangedProducts={withExchangedProducts}
        />
      </LegContainer>
    </div>
  );
};

Leg.propTypes = {
  direction: PropTypes.string,
  serviceType: PropTypes.shape({
    brandCode: PropTypes.string,
    code: PropTypes.string.isRequired,
    name: PropTypes.string
  }),
  isAFareAddOn: PropTypes.bool,
  origin: PropTypes.shape({
    name: PropTypes.string,
    shortCode: PropTypes.string
  }),
  destination: PropTypes.shape({
    name: PropTypes.string,
    shortCode: PropTypes.string
  }),
  currency: PropTypes.string,
  journey: legShape,
  afterSaleRuleOverride: afterSalesRule,
  numberOfPassengers: PropTypes.number,
  numberOfActivePassengers: PropTypes.number,
  date: PropTypes.string,
  trainNumber: PropTypes.string,
  arrivalTime: PropTypes.string,
  departureTime: PropTypes.node,
  includeFareConditions: PropTypes.bool,
  classOfService: classOfService,
  customerTransactionRef: PropTypes.string,
  children: PropTypes.node,
  withExchangedProducts: PropTypes.arrayOf(
    PropTypes.shape({
      products: productsShape
    })
  ),
  bookingReference: PropTypes.string.isRequired,
  status: PropTypes.string
};

Leg.defaultProps = {
  includeFareConditions: true
};

export default injectIntl(Leg);
